import * as dto from 'client/dto';
import { RiskLikelihood, RiskMagnitude } from 'models/RiskCategories';
import utils from 'utils/assetUtils';
import { getRiskLikelihood, getRiskLikelihoodAvg, getRiskMagnitude } from 'utils/modelHelpers';
import { nameof } from 'utils/typeHelpers';

import riskLayer from 'map/riskLayer';
import { EsriPackages } from 'models/common-map-types';

export type Esri3ZoomLevels =
  | 0
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12
  | 13
  | 14
  | 15
  | 16
  | 17
  | 18
  | 19
  | 20
  | 21
  | 22
  | 23;

const AIRPORT_PIN_ICON = color => `
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="5" cy="5" r="4.75" fill="${color}" stroke="white" stroke-width="0.5"/>
    <path d="M7.74308 6.12373C7.87128 6.16179 8 6.06573 8 5.932V5.71385C8 5.64327 7.9628 5.57791 7.90211 5.54188L5.57158 4.15812C5.51089 4.12209 5.47368 4.05673 5.47368 3.98615V2.45C5.47368 2.201 5.26211 2 5 2C4.73789 2 4.52632 2.201 4.52632 2.45V3.98615C4.52632 4.05673 4.48911 4.12209 4.42842 4.15812L2.09789 5.54188C2.0372 5.57791 2 5.64327 2 5.71385V5.932C2 6.06573 2.12872 6.16179 2.25692 6.12373L4.2694 5.52627C4.3976 5.48821 4.52632 5.58427 4.52632 5.718V6.99693C4.52632 7.0616 4.49504 7.12228 4.44237 7.15981L3.97868 7.49019C3.92601 7.52772 3.89474 7.5884 3.89474 7.65307V7.73848C3.89474 7.87025 4.01995 7.96601 4.14713 7.93149L4.94761 7.71422C4.98192 7.70491 5.01808 7.70491 5.05239 7.71422L5.85287 7.93149C5.98005 7.96601 6.10526 7.87025 6.10526 7.73848V7.65307C6.10526 7.5884 6.07399 7.52772 6.02132 7.49019L5.55763 7.15981C5.50496 7.12228 5.47368 7.0616 5.47368 6.99693V5.718C5.47368 5.58427 5.6024 5.48821 5.7306 5.52627L7.74308 6.12373Z" fill="white"/>
  </svg>
  `;
const AIRPORT_PIN_LOD_DOT = color => `
  <svg width="6" height="6" viewBox="0 0 6 6" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="3" cy="3" r="3" fill="${color}" stroke="white" stroke-width="0.5"/>
  </svg>
  `;
const OUTBREAK_HIGHLIGHT_COLOR = [63, 75, 86, 51];

export const AirportFeatureCollectionIconLayer = {
  featureSet: {},
  layerDefinition: {
    geometryType: 'esriGeometryPoint',
    fields: [],
  },
};
export const AirportFeatureCollectionRiskLayer = {
  featureSet: {},
  layerDefinition: {
    geometryType: 'esriGeometryPoint',
    fields: [
      {
        name: nameof<AirportMapPinAttributes>('AIRPORT_NAME'),
        alias: nameof<AirportMapPinAttributes>('AIRPORT_NAME'),
        type: 'esriFieldTypeString',
      },
      {
        name: nameof<AirportMapPinAttributes>('LOCATION_NAME'),
        alias: nameof<AirportMapPinAttributes>('LOCATION_NAME'),
        type: 'esriFieldTypeString',
      },
      {
        name: nameof<AirportMapPinAttributes>('RISK_MAGNITUDE_TEXT'),
        alias: nameof<AirportMapPinAttributes>('RISK_MAGNITUDE_TEXT'),
        type: 'esriFieldTypeString',
      },
      {
        name: nameof<AirportMapPinAttributes>('RISK_MAGNITUDE_CLASSIFIER'),
        alias: nameof<AirportMapPinAttributes>('RISK_MAGNITUDE_CLASSIFIER'),
        type: 'esriFieldTypeString',
      },
      {
        name: nameof<AirportMapPinAttributes>('LIKELIHOOD_TEXT'),
        alias: nameof<AirportMapPinAttributes>('LIKELIHOOD_TEXT'),
        type: 'esriFieldTypeString',
      },
    ],
  },
};

export function MakeAirportIconLayerLodRendererLikelihood(
  esriPackages: EsriPackages,
  zoomBreakpoint: Esri3ZoomLevels
) {
  return new esriPackages.ScaleDependentRenderer({
    rendererInfos: [
      {
        minZoom: 0,
        maxZoom: zoomBreakpoint - 1,
        renderer: new esriPackages.UniqueValueRenderer({
          type: 'uniqueValue',
          field1: nameof<AirportMapPinAttributes>('LIKELIHOOD_TEXT'),
          classificationMethod: 'esriClassifyManual',
          defaultSymbol: {
            type: 'esriPMS',
            imageData: utils.encode(AIRPORT_PIN_ICON('#4C5761')), // NOTE: original, pre PT-1833
            contentType: 'image/svg+xml',
            width: 9,
            height: 9,
          },
          uniqueValueInfos: riskLayer.airportPinLikelihoodUniqueValues.map(x => ({
            value: x.value,
            symbol: riskLayer.LODTheseAirportLikelihoodPins.includes(x.value)
              ? {
                  type: 'esriPMS',
                  imageData: utils.encode(AIRPORT_PIN_LOD_DOT(x.color)),
                  contentType: 'image/svg+xml',
                  width: 6,
                  height: 6,
                }
              : {
                  type: 'esriPMS',
                  imageData: utils.encode(AIRPORT_PIN_ICON(x.color)),
                  contentType: 'image/svg+xml',
                  width: 9,
                  height: 9,
                },
          })),
        }),
      },
      {
        minZoom: zoomBreakpoint,
        maxZoom: 23,
        renderer: new esriPackages.UniqueValueRenderer({
          type: 'uniqueValue',
          field1: nameof<AirportMapPinAttributes>('LIKELIHOOD_TEXT'),
          classificationMethod: 'esriClassifyManual',
          defaultSymbol: {
            type: 'esriPMS',
            imageData: utils.encode(AIRPORT_PIN_ICON('#4C5761')), // NOTE: original, pre PT-1833
            contentType: 'image/svg+xml',
            width: 9,
            height: 9,
          },
          uniqueValueInfos: riskLayer.airportPinLikelihoodUniqueValues.map(x => ({
            value: x.value,
            symbol: {
              type: 'esriPMS',
              imageData: utils.encode(AIRPORT_PIN_ICON(x.color)),
              contentType: 'image/svg+xml',
              width: 9,
              height: 9,
            },
          })),
        }),
      },
    ],
  });
}

export function MakeAirportIconLayerLodRendererMagnitude(
  esriPackages: EsriPackages,
  zoomBreakpoint: Esri3ZoomLevels
) {
  return new esriPackages.ScaleDependentRenderer({
    rendererInfos: [
      {
        minZoom: 0,
        maxZoom: zoomBreakpoint - 1,
        renderer: new esriPackages.UniqueValueRenderer({
          type: 'uniqueValue',
          field1: nameof<AirportMapPinAttributes>('RISK_MAGNITUDE_CLASSIFIER'),
          classificationMethod: 'esriClassifyManual',
          defaultSymbol: null,
          uniqueValueInfos: riskLayer.airportRiskValueCollection.map(x => ({
            value: x.value,
            symbol: {
              type: 'esriSMS',
              style: 'esriSMSCircle',
              color: OUTBREAK_HIGHLIGHT_COLOR,
              size: x.sizeLOD !== undefined ? x.sizeLOD : x.size,
            },
          })),
        }),
      },
      {
        minZoom: zoomBreakpoint,
        maxZoom: 23,
        renderer: new esriPackages.UniqueValueRenderer({
          type: 'uniqueValue',
          field1: nameof<AirportMapPinAttributes>('RISK_MAGNITUDE_CLASSIFIER'),
          classificationMethod: 'esriClassifyManual',
          defaultSymbol: null,
          uniqueValueInfos: riskLayer.airportRiskValueCollection.map(x => ({
            value: x.value,
            symbol: {
              type: 'esriSMS',
              style: 'esriSMSCircle',
              color: OUTBREAK_HIGHLIGHT_COLOR,
              size: x.size,
            },
          })),
        }),
      },
    ],
  });
}

export type AirportMapPinVM = {
  StationName: string;
  CityDisplayName: string;
  StationCode: string;
  x: number;
  y: number;
  RiskLikelihoodText: RiskLikelihood;
  RiskMagnitudeText: RiskMagnitude;
  RiskMagnitudeClassifier: RiskMagnitude;
};

export type AirportMapPinAttributes = {
  sourceData: AirportMapPinVM;
  AIRPORT_NAME: string;
  LOCATION_NAME: string;
  RISK_MAGNITUDE_TEXT: RiskMagnitude;
  RISK_MAGNITUDE_CLASSIFIER: RiskMagnitude;
  LIKELIHOOD_TEXT: RiskLikelihood;
};

export function MapAirportModel2AirportPinVM(
  responseData: dto.GetAirportRiskModel[],
  postprocessing: 'treatLowLikelihoodAsNegligibleRisk' | false
): AirportMapPinVM[] {
  if (!responseData) {
    return [];
  }

  const airportData = responseData
    .filter(e => !isNaN(e.latitude) && !isNaN(e.longitude) && e.latitude !== 0 && e.longitude !== 0)
    .sort(
      (x, y) => getRiskLikelihoodAvg(x.importationRisk) - getRiskLikelihoodAvg(y.importationRisk)
    ) // NOTE: orders pins in ASC order by likelihood (avg min/max probability) so that later higher risk pins (red) show up on top
    .map(({ name, city, code, longitude, latitude, importationRisk: risk }) => ({
      StationName: name,
      CityDisplayName: city,
      StationCode: code,
      x: Number(longitude),
      y: Number(latitude),
      RiskLikelihoodText: getRiskLikelihood(risk),
      RiskMagnitudeText: getRiskMagnitude(risk),
      RiskMagnitudeClassifier:
        postprocessing === 'treatLowLikelihoodAsNegligibleRisk' && getRiskLikelihood(risk) === 'Low'
          ? 'Negligible'
          : getRiskMagnitude(risk),
    }))
    .filter(x => riskLayer.ShowOnlyTheseAirportLikelihoodPins.includes(x.RiskLikelihoodText));

  return airportData;
}

export function createAirportGraphic(esriPackages: EsriPackages, item: AirportMapPinVM) {
  const { Point, Graphic } = esriPackages;
  const graphic = new Graphic(new Point(item));
  graphic.setAttributes({
    sourceData: item,
    AIRPORT_NAME: item.StationName || '',
    LOCATION_NAME: item.CityDisplayName || '',
    RISK_MAGNITUDE_TEXT: item.RiskMagnitudeText,
    RISK_MAGNITUDE_CLASSIFIER: item.RiskMagnitudeClassifier,
    LIKELIHOOD_TEXT: item.RiskLikelihoodText,
  } as AirportMapPinAttributes);

  return graphic;
}
